import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
// Publish/unpublish is an eye pair — deliberately *not* an arrow, so it cannot
// be confused with the pull-to-top action next to it.
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import {
    fetchArticles,
    deleteArticle,
    toggleArticleVisibility,
    toggleArticlePullUp,
    selectAllArticles,
    selectArticleStatus,
    selectArticleError,
} from './ArticleSlice';
import { selectArticleTypeDict, selectAllArticleTypes } from './ArticleTypeSlice';
import { useIsMobile } from '../../theme';

// How many rows are rendered before "Mostrar mais". A page of results beats a
// pager on touch: scrolling is the gesture people already use.
const PAGE_SIZE = 20;

const SORTS = {
    recent: { label: 'Mais recentes', compare: (a, b) => b._orderAt - a._orderAt },
    oldest: { label: 'Mais antigos', compare: (a, b) => a._orderAt - b._orderAt },
    updated: { label: 'Alterados há menos tempo', compare: (a, b) => (b._updatedAt || 0) - (a._updatedAt || 0) },
    title: { label: 'Título (A–Z)', compare: (a, b) => a.title.localeCompare(b.title, 'pt') },
};

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—';

// Delete Confirmation Modal Component
const DeleteConfirmDialog = ({ open, onClose, onConfirm, articleTitle }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Apagar conteúdo</DialogTitle>
        <DialogContent>
            <DialogContentText>Tem a certeza que quer apagar “{articleTitle}”? Esta ação não pode ser desfeita.</DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={onConfirm} color='error' variant='contained'>
                Apagar
            </Button>
        </DialogActions>
    </Dialog>
);

// One row of the list. The whole card is the edit target — a double-click was
// undiscoverable on a desktop and impossible on a phone.
const ArticleRow = ({ row, compact, onEdit, onTogglePublish, onTogglePullUp, onDelete }) => {
    const [menuEl, setMenuEl] = useState(null);
    const closeMenu = () => setMenuEl(null);
    const run = (fn) => () => {
        closeMenu();
        fn();
    };

    const publishBlocked = !row.published && !row.hasCover;

    const publishButton = (
        <Tooltip title={publishBlocked ? 'Não pode publicar sem imagem de capa' : row.published ? 'Despublicar (esconder do site)' : 'Publicar'}>
            {/* span wrapper so the tooltip still shows on a disabled button */}
            <span>
                <IconButton
                    color={row.published ? 'success' : 'primary'}
                    disabled={publishBlocked}
                    onClick={() => onTogglePublish(row.id)}
                    aria-label={row.published ? 'Despublicar' : 'Publicar'}
                >
                    {publishBlocked ? <BlockIcon /> : row.published ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Paper
            variant='outlined'
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: { xs: 1, md: 1.5 },
                // A published item reads at a glance from the left edge.
                borderLeft: 4,
                borderLeftColor: row.published ? 'success.main' : 'divider',
            }}
        >
            <Box
                onClick={() => onEdit(row.raw || row)}
                sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onEdit(row.raw || row)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{row.title}</Typography>
                    {row.draft && <Chip label='RASCUNHO' size='small' color='warning' variant='outlined' sx={{ height: 20, fontSize: '0.65rem' }} />}
                    {row.pulledUp && <Chip label='TOPO' size='small' color='primary' variant='outlined' sx={{ height: 20, fontSize: '0.65rem' }} />}
                    {!row.hasCover && <Chip label='SEM CAPA' size='small' color='error' variant='outlined' sx={{ height: 20, fontSize: '0.65rem' }} />}
                </Box>
                <Typography variant='caption' color='text.secondary' component='div' sx={{ mt: 0.25 }}>
                    {row.type} · v{row.version} · criado {row.creationDate}
                    {compact ? '' : ` · alterado ${row.lastUpdate}`}
                </Typography>
            </Box>

            {/* Desktop shows every action; a phone keeps only the one that is
                used constantly and folds the rest into a menu. */}
            {compact ? (
                <>
                    {publishButton}
                    <IconButton onClick={(e) => setMenuEl(e.currentTarget)} aria-label='Mais ações'>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu}>
                        <MenuItem onClick={run(() => onEdit(row.raw || row))}>
                            <ListItemIcon>
                                <EditIcon fontSize='small' />
                            </ListItemIcon>
                            Editar
                        </MenuItem>
                        <MenuItem onClick={run(() => onTogglePullUp(row.id))}>
                            <ListItemIcon>
                                <VerticalAlignTopIcon fontSize='small' />
                            </ListItemIcon>
                            {row.pulledUp ? 'Retirar do topo' : 'Puxar para o topo'}
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={run(() => onDelete(row.id))} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize='small' color='error' />
                            </ListItemIcon>
                            Apagar
                        </MenuItem>
                    </Menu>
                </>
            ) : (
                <>
                    <Tooltip title={row.pulledUp ? 'Retirar do topo (volta à data de criação)' : 'Puxar para o topo das listagens'}>
                        <IconButton color={row.pulledUp ? 'primary' : 'default'} onClick={() => onTogglePullUp(row.id)} aria-label='Topo'>
                            <VerticalAlignTopIcon />
                        </IconButton>
                    </Tooltip>
                    {publishButton}
                    <Tooltip title='Editar'>
                        <IconButton onClick={() => onEdit(row.raw || row)} aria-label='Editar'>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Apagar'>
                        <IconButton color='error' onClick={() => onDelete(row.id)} aria-label='Apagar'>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )}
        </Paper>
    );
};

// #region ARTICLES GRID
const ArticlesGrid = ({ onEditArticle, onCreate }) => {
    const dispatch = useDispatch();
    const articles = useSelector(selectAllArticles);
    const loading = useSelector(selectArticleStatus) === 'loading';
    const error = useSelector(selectArticleError);
    const articleTypeDict = useSelector(selectArticleTypeDict);
    const articleTypes = useSelector(selectAllArticleTypes);
    const isMobile = useIsMobile();

    const [deleteDialog, setDeleteDialog] = useState({ open: false, articleId: null, articleTitle: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [newMenuEl, setNewMenuEl] = useState(null);

    const transformArticles = useCallback((articles, articleTypeDict) => {
        return articles.map((a) => {
            const id = a.idArticle || a.id || a.id_article; // adapt to possible backend naming
            const created = a.createdAt || a.creationDate || a.created_at || null;
            const updated = a.updatedAt || a.updated_at || a.lastUpdate || null;
            const version = (a.lastVersion || a.version || 1).toString();
            const createdTs = created ? Date.parse(created) : null;
            const updatedTs = updated ? Date.parse(updated) : null;
            // Set when the article was manually pulled to the top of the public
            // listings. The site orders by it as if it were the creation date.
            const pulledUpTs = a.pulledUpAt ? Date.parse(a.pulledUpAt) : null;
            const safeCreated = isNaN(createdTs) ? 0 : createdTs || 0;
            const safeUpdated = isNaN(updatedTs) ? 0 : updatedTs || 0;
            const effectiveTs = Math.max(safeCreated, safeUpdated); // greatest of creation/update
            const safePulledUp = isNaN(pulledUpTs) ? null : pulledUpTs;

            // Get article type nickname
            const articleType = articleTypeDict[a.idArticleType];
            const typeNickname = articleType ? articleType.nickname : 'Artigo';

            return {
                id,
                raw: a, // keep original for edit callback if needed
                idArticleType: a.idArticleType, // used by the type filter
                type: typeNickname,
                title: a.title || '(sem título)',
                creationDate: formatDate(created),
                lastUpdate: formatDate(updated),
                version,
                published: Boolean(a.visible),
                draft: Boolean(a.draft),
                // An article cannot be published without a cover image.
                hasCover: Boolean(a.idCoverImage || a.coverImage),
                pulledUp: Boolean(a.pulledUpAt),
                // Raw timestamps & composite for deterministic sorting
                _createdAt: createdTs,
                _updatedAt: updatedTs,
                // What the public listings order by: a pulled-up article sorts by
                // when it was pulled up, so the list never contradicts the site.
                _orderAt: safePulledUp || effectiveTs,
            };
        });
    }, []);

    const [titleQuery, setTitleQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState(''); // '' = all types
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'published' | 'unpublished' | 'draft'
    const [sortKey, setSortKey] = useState('recent');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    // Debounce user input to avoid re-filtering on every keystroke
    useEffect(() => {
        const h = setTimeout(() => setDebouncedQuery(titleQuery.trim().toLowerCase()), 250);
        return () => clearTimeout(h);
    }, [titleQuery]);

    const activeFilterCount = (typeFilter ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0) + (sortKey !== 'recent' ? 1 : 0);

    // Filter (type + status + title) then sort.
    const rows = useMemo(() => {
        const base = transformArticles(articles, articleTypeDict);
        const filtered = base.filter((r) => {
            if (typeFilter && String(r.idArticleType) !== String(typeFilter)) return false;
            if (statusFilter === 'published' && !r.published) return false;
            if (statusFilter === 'unpublished' && r.published) return false;
            if (statusFilter === 'draft' && !r.draft) return false;
            if (debouncedQuery && !(r.title || '').toLowerCase().includes(debouncedQuery)) return false;
            return true;
        });
        return filtered.slice().sort(SORTS[sortKey].compare);
    }, [articles, articleTypeDict, transformArticles, debouncedQuery, typeFilter, statusFilter, sortKey]);

    // A narrowed result set should start from the top again.
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [debouncedQuery, typeFilter, statusFilter, sortKey]);

    useEffect(() => {
        dispatch(fetchArticles());
    }, [dispatch]);

    // Show error toast when error changes, hide when error is cleared
    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: `Erro: ${error}`, severity: 'error' });
        } else {
            // Close the snackbar when error is cleared
            setSnackbar((prev) => (prev.severity === 'error' ? { ...prev, open: false } : prev));
        }
    }, [error]);

    const handleSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleDelete = (id) => {
        const article = rows.find((row) => row.id === id);
        setDeleteDialog({
            open: true,
            articleId: id,
            articleTitle: article?.title || 'este conteúdo',
        });
    };

    const handleDeleteConfirm = async () => {
        const { articleId } = deleteDialog;
        setDeleteDialog({ open: false, articleId: null, articleTitle: '' });

        await dispatch(deleteArticle(articleId));
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ open: false, articleId: null, articleTitle: '' });
    };

    const handleTogglePublish = async (id) => {
        // Find the current article to get its current visibility state
        const article = rows.find((row) => row.id === id);
        if (!article) {
            console.error('Article not found:', id);
            return;
        }

        await dispatch(toggleArticleVisibility({ articleId: id, isVisible: !article.published }));
    };

    const handleTogglePullUp = async (id) => {
        const article = rows.find((row) => row.id === id);
        if (!article) {
            console.error('Article not found:', id);
            return;
        }

        await dispatch(toggleArticlePullUp({ articleId: id, pulledUp: !article.pulledUp }));
    };

    const handleNewClick = (event) => {
        // One type means there is nothing to choose — skip the menu.
        if (articleTypes.length <= 1) return onCreate(articleTypes[0]?.idArticleType ?? null);
        setNewMenuEl(event.currentTarget);
    };

    const visibleRows = rows.slice(0, visibleCount);

    // #region RETURN
    return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: { xs: 1, md: 2 }, gap: 1.5 }}>
            {/* Toolbar: search is always reachable; the rest folds away on a
                phone behind a button that shows how many filters are on. */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                    value={titleQuery}
                    onChange={(e) => setTitleQuery(e.target.value)}
                    placeholder='Procurar por título…'
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon fontSize='small' />
                            </InputAdornment>
                        ),
                        endAdornment: titleQuery ? (
                            <InputAdornment position='end'>
                                <IconButton size='small' onClick={() => setTitleQuery('')} aria-label='Limpar procura'>
                                    <ClearIcon fontSize='small' />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                {isMobile && (
                    <IconButton onClick={() => setFiltersOpen((open) => !open)} aria-label='Filtros' color={filtersOpen ? 'primary' : 'default'}>
                        <Badge badgeContent={activeFilterCount} color='primary'>
                            <TuneIcon />
                        </Badge>
                    </IconButton>
                )}
                <Button
                    variant='contained'
                    startIcon={<AddIcon />}
                    onClick={handleNewClick}
                    // Until the types have loaded there is no section to file a
                    // new item under, and creating one would silently land it in
                    // whichever type happens to be id 1.
                    disabled={!articleTypes.length}
                    sx={{ flexShrink: 0, px: { xs: 1.5, md: 2 } }}
                >
                    Novo
                </Button>
                <Menu anchorEl={newMenuEl} open={Boolean(newMenuEl)} onClose={() => setNewMenuEl(null)}>
                    {articleTypes.map((type) => (
                        <MenuItem
                            key={type.idArticleType}
                            onClick={() => {
                                setNewMenuEl(null);
                                onCreate(type.idArticleType);
                            }}
                        >
                            {type.display_name || type.name}
                            {type.form === 'etiqueta' ? ' (ficha)' : ''}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>

            <Collapse in={!isMobile || filtersOpen}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                    }}
                >
                    <TextField select label='Tipo' value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                        <option value=''>Todos</option>
                        {articleTypes.map((t) => (
                            <option key={t.idArticleType} value={t.idArticleType}>
                                {t.display_name || t.nickname || t.name || t.idArticleType}
                            </option>
                        ))}
                    </TextField>
                    <TextField select label='Estado' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                        <option value='all'>Todos</option>
                        <option value='published'>Publicados</option>
                        <option value='unpublished'>Não publicados</option>
                        <option value='draft'>Rascunhos</option>
                    </TextField>
                    <TextField select label='Ordenar' value={sortKey} onChange={(e) => setSortKey(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                        {Object.entries(SORTS).map(([key, { label }]) => (
                            <option key={key} value={key}>
                                {label}
                            </option>
                        ))}
                    </TextField>
                </Box>
            </Collapse>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 24 }}>
                <Typography variant='caption' color='text.secondary'>
                    {rows.length} {rows.length === 1 ? 'resultado' : 'resultados'}
                </Typography>
                {loading && <CircularProgress size={14} />}
            </Box>

            {/* The list itself. One column everywhere: a card reads the same on a
                phone and on a desktop, and there is no horizontal scroll to trap
                a thumb (the old DataGrid needed one for its six columns). */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
                {!loading && !rows.length && (
                    <Paper variant='outlined' sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color='text.secondary'>
                            {articles.length ? 'Nada corresponde aos filtros.' : 'Ainda não há conteúdos. Comece com “Novo”.'}
                        </Typography>
                    </Paper>
                )}

                {visibleRows.map((row) => (
                    <ArticleRow
                        key={row.id}
                        row={row}
                        compact={isMobile}
                        onEdit={onEditArticle}
                        onTogglePublish={handleTogglePublish}
                        onTogglePullUp={handleTogglePullUp}
                        onDelete={handleDelete}
                    />
                ))}

                {rows.length > visibleCount && (
                    <Button onClick={() => setVisibleCount((count) => count + PAGE_SIZE)} sx={{ alignSelf: 'center', mt: 1 }}>
                        Mostrar mais ({rows.length - visibleCount})
                    </Button>
                )}
            </Box>

            <DeleteConfirmDialog
                open={deleteDialog.open}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                articleTitle={deleteDialog.articleTitle}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant='filled' sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ArticlesGrid;
